import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { apiClient } from "../../api/ApiClient";
import { queryKeys } from "../../api/queryKeys";
import { useAutomationAuthorizations } from "../../contexts/AutomationAuthorizationContext";
import { extractAuthorizations } from "../../helpers/authorizations";
import { expandSourceFilter } from "../../helpers/sourceFilter";
import { PaginationConfig } from "../ui/pagination/Pagination";
import { ContentItem } from "./ContentItems";
import PagesList, { Page } from "./components/PagesList";

interface PagesStats {
  active: number;
  total: number;
}

interface PageType {
  id: number;
  path_and_query?: string;
  approved?: number;
  total?: number;
}

type ContentProps = {
  data: ContentItem[];
  pagination: PaginationConfig;
  setPagination: (pagination: PaginationConfig) => void;
  isPagesLoading: boolean;
  isInitialLoading: boolean;
  group?: "enhancement" | "ai" | "schema" | "ai_instructions";
  setSearchTerm: (searchTerm: string) => void;
  searchTerm: string;
  title?: string;
  description?: string;
  currentPage?: ContentItem | undefined | null;
  setCurrentPage?: (page: PageType) => void;
  onMutationSuccess?: (showLoadingState?: boolean) => void;
  pagesStats?: PagesStats;
  isRefetching?: boolean;
  isFetchingStats?: boolean;
  sourceFilter?: "auto" | "manual" | undefined;
  outdatedFilter?: boolean | undefined;
};

const Content = ({
  data,
  pagination,
  setPagination,
  isPagesLoading,
  isInitialLoading,
  group = "ai",
  searchTerm,
  setSearchTerm,
  title,
  description,
  currentPage,
  setCurrentPage,
  onMutationSuccess,
  pagesStats,
  isRefetching = false,
  isFetchingStats,
  sourceFilter,
  outdatedFilter,
}: ContentProps) => {
  const [currentItem, setCurrentItem] = useState<ContentItem[]>([]);
  const [itemAuthorizations, setItemAuthorizations] = useState<string[][]>([]);
  const { updateAuthorizations } = useAutomationAuthorizations();

  const { id } = useParams();

  const {
    data: pendingStatusesAndPermissions,
    isFetching: isPendingStatusesAndPermissionsFetching,
  } = useQuery({
    queryKey: queryKeys.pendingStatusesAndPermissions(group),
    queryFn: async () => {
      try {
        const response = await apiClient.sites.widgetCustomizationActionsDetail(
          Number(id),
          {
            "page[size]": 50,
            q: { status_eq: "pending" },
          },
          {
            headers: {
              "Authorize-Collection-Actions": "create",
            },
          },
        );

        const pendingItems =
          response?.data?.["widget/customization_actions"] || [];
        const auth = extractAuthorizations(response?.data);
        const approvePermissions = auth?.collection?.includes("create");

        return {
          pendingItems,
          approvePermission: !!approvePermissions,
        };
      } catch (error) {
        const status = error instanceof Response ? error.status : null;

        if (status === 403) {
          return { pendingItems: [], approvePermission: false, status: 403 };
        }

        throw error;
      }
    },
    retry: (failureCount, error) => {
      if (error instanceof Response && error.status === 403) {
        return true;
      }

      return failureCount < 3;
    },
    retryDelay: (attemptIndex, error) => {
      if (error instanceof Response && error.status === 403) {
        return 1000;
      }

      return Math.min(1000 * 2 ** attemptIndex, 30000);
    },
    refetchInterval: (query) => {
      if (
        query?.state?.data?.pendingItems &&
        query.state.data.pendingItems.length > 0
      ) {
        return 1000;
      }

      if (query?.state?.data?.status === 403) {
        return 1000;
      }

      return false;
    },
  });
  const pageId = currentPage?.id;

  const { isFetching: isCustomizationsFetching } = useQuery({
    queryKey: queryKeys.contentAutomations(
      group,
      pageId,
      sourceFilter,
      outdatedFilter,
    ),
    queryFn: async () => {
      // `source_in` is a ransack predicate not in the generated types; the
      // outer cast covers it. See expandSourceFilter for source semantics.
      const apiSources = expandSourceFilter(sourceFilter);
      const customizationsQuery =
        apiSources || outdatedFilter
          ? ({
              q: {
                ...(apiSources ? { source_in: apiSources } : {}),
                ...(outdatedFilter ? { outdated_eq: true } : {}),
              },
            } as Parameters<
              typeof apiClient.sites.pagesWidgetCustomizationsDetail
            >[3])
          : undefined;
      const headers = {
        "Authorize-Collection-Actions": "update",
        "Authorize-Member-Actions":
          "generate_title,generate_description,generate_schema,generate_ai_instructions,destroy,update",
      };
      // ai.md lives behind a dedicated endpoint (no `variant` segment); the
      // other variants share the generic one.
      const response =
        group === "ai_instructions"
          ? await apiClient.sites.pagesWidgetCustomizationsAiInstructionsDetail(
              Number(id),
              pageId as number,
              customizationsQuery as Parameters<
                typeof apiClient.sites.pagesWidgetCustomizationsAiInstructionsDetail
              >[2],
              { headers },
            )
          : await apiClient.sites.pagesWidgetCustomizationsDetail(
              Number(id),
              pageId as number,
              group,
              customizationsQuery,
              { headers },
            );
      const customizations = response?.data?.["widget/customizations"] || [];
      setCurrentItem(customizations as ContentItem[]);

      const authorizations = extractAuthorizations(response?.data);
      const memberAuths = authorizations?.member || [];
      setItemAuthorizations(memberAuths);
      updateAuthorizations(memberAuths.flat());

      return customizations;
    },
    enabled: !!pageId,
  });

  return (
    <PagesList
      setCurrentItem={setCurrentItem}
      itemAuthorizations={itemAuthorizations}
      isFetchingStats={isFetchingStats}
      pagesStats={pagesStats}
      title={title}
      description={description}
      onMutationSuccess={onMutationSuccess}
      group={group}
      isLoading={isCustomizationsFetching}
      data={data.filter((item) => item.id !== undefined) as Page[]}
      pagination={pagination}
      setPagination={setPagination}
      isPagesLoading={isPagesLoading || isRefetching}
      isInitialLoading={isInitialLoading}
      currentItem={currentItem}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      // TODO: `|| true` makes permission always true — investigate why this was added
      approvePermission={
        pendingStatusesAndPermissions?.approvePermission || true
      }
      pendingStatuses={pendingStatusesAndPermissions?.pendingItems || []}
      isPendingStatusesAndPermissionsFetching={
        isPendingStatusesAndPermissionsFetching
      }
      sourceFilter={sourceFilter}
      outdatedFilter={outdatedFilter}
    />
  );
};

export default Content;
